import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  calculateCartTotals,
  calculateShippingFee,
  calculateShippingCosts
} from 'components/cart/utils';
import { productNeedsConditionChoice, BOOK_CONDITION, cartLinesMatch } from 'utils/bookCondition';

const initialState = {
  items: [],
  countItems: 0,
  subtotal: 0.0,
  iva: 0.0,
  total: 0.0,
  shippingCosts: 0.0,
  totalWithDiscount: 0.0,
  discountCart: {
    type: '',
    quantity: 0,
    has_discount: false,
    code: '',
  },
  lastAddedItem: null,
  lastRemovedItem: null,
  isStorePickup: false,
  errorAmountPrice: false,
  isGiftOrder: false,
  giftData: {},
  giftCard: {},
  appliedCoupon: null,
  hasGiftCard: false,
  isShippingAvailable: { availability: true, message: '' },
  shippingCartAddress: {},
  billingCartAddress: {},
  contactInfo: {
    name: '',
    email: '',
  },
};

export const useCartStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      addItem: (payload) => {
        const { product, province, shippingCost, shippingCostIslands, minPurchaseForFreeShipping, quantity, setQuantity, isConcourseUser, concourseDiscount, bookCondition: bookConditionArg } = payload;

        if (!product || typeof product !== 'object') {
          console.error('Producto no válido:', product);
          return { success: false, message: 'Producto no válido' };
        }

        if (typeof product.iva !== 'number') {
          product.iva = 4;
        }

        const needsChoice = productNeedsConditionChoice(product);
        const conditionFromProduct =
          product.book_condition ??
          product.producto?.book_condition ??
          product.producto?.bookCondition;
        const explicitCondition =
          bookConditionArg !== undefined && bookConditionArg !== null && bookConditionArg !== ''
            ? bookConditionArg
            : conditionFromProduct !== undefined &&
                conditionFromProduct !== null &&
                conditionFromProduct !== ''
              ? conditionFromProduct
              : null;

        let resolvedBookCondition = null;
        if (needsChoice) {
          resolvedBookCondition = explicitCondition || BOOK_CONDITION.NEW;
        } else if (explicitCondition) {
          resolvedBookCondition = explicitCondition;
        }
        const lineProbe = { id: product.id, book_condition: resolvedBookCondition };

        // Aplicar descuento de concurso si existe y el usuario es concurso
        // Aplicar descuento de producto si viene del API (campo discount)
        // Siempre usar el precio original del producto que viene del backend
        const state = get();
        const itemExists = state.items?.find((item) => cartLinesMatch(item, lineProbe));
        
        // unit_price viene calculado por el backend según la condición seleccionada del libro.
        const productOriginalPrice = product.unit_price ?? itemExists?.unit_price ?? product.price ?? product.precio_venta_publico ?? 0;
        
        // El precio original siempre viene del producto, no del item existente
        // Si el item ya existe y tiene originalPrice guardado, usarlo; si no, usar el precio del producto
        const originalProductPrice = (product.unit_price == null && itemExists?.originalPrice && itemExists.originalPrice > 0) 
          ? itemExists.originalPrice 
          : productOriginalPrice;
        
        let productPrice = originalProductPrice;
        let originalPrice = originalProductPrice;
        
        // 1. Aplicar descuento de producto (campo discount del API) - se aplica a todos los usuarios
        const productDiscount = Number(product.discount) || 0;
        if (productDiscount > 0) {
          const discountAmount = (originalProductPrice * productDiscount) / 100;
          productPrice = Number((originalProductPrice - discountAmount).toFixed(2));
          originalPrice = originalProductPrice;
        }
        
        // 2. Aplicar descuento de concurso si el usuario es concurso (se aplica sobre el precio ya con descuento de producto)
        if (isConcourseUser && concourseDiscount && concourseDiscount > 0) {
          const baseForConcourse = productPrice; // Precio base para concurso (puede ser ya con descuento producto)
          const discountAmount = (baseForConcourse * concourseDiscount) / 100;
          productPrice = Number((baseForConcourse - discountAmount).toFixed(2));
          originalPrice = originalPrice || baseForConcourse;
        }
        
        // Si se especifica una cantidad, usarla; si no, agregar 1
        const quantityToAdd = quantity !== undefined ? quantity : 1;
        
        let newItems;
        if (itemExists) {
          // Si setQuantity es true, establecer la cantidad; si no, sumar
          // Siempre aplicar el descuento si el usuario es concurso
          newItems = state.items.map(item => {
            if (cartLinesMatch(item, lineProbe)) {
              // Si el usuario es concurso, aplicar el descuento
              return {
                ...item,
                quantity: setQuantity ? quantityToAdd : item.quantity + quantityToAdd,
                price: productPrice,
                unit_price: product.unit_price ?? productPrice,
                originalPrice: (productDiscount > 0 || (isConcourseUser && concourseDiscount && concourseDiscount > 0)) ? originalPrice : item.originalPrice,
                discount: product.discount ?? item.discount
              };
            }
            return item;
          });
        } else {
          // Crear nuevo producto con precio aplicado (con descuento si corresponde)
          const newProduct = { 
            ...product, 
            quantity: quantityToAdd,
            price: productPrice,
            unit_price: product.unit_price ?? productPrice,
            originalPrice: (productDiscount > 0 || (isConcourseUser && concourseDiscount)) ? originalPrice : undefined,
            discount: product.discount
          };
          if (resolvedBookCondition) {
            newProduct.book_condition = resolvedBookCondition;
          } else if (explicitCondition) {
            newProduct.book_condition = explicitCondition;
          }
          newItems = [...state.items, newProduct];
        }

        // Calcular nuevo costo de envío base
        const baseShippingCost = calculateShippingFee(province || '', newItems, shippingCost, shippingCostIslands);

        // Recalcular todos los totales primero para obtener el total del carrito
        const totals = calculateCartTotals(newItems, 0, state.hasGiftCard ? state.giftCard : null);

        // Aplicar envío gratuito si el total supera el mínimo o si es cliente concurso
        const finalShippingCosts = calculateShippingCosts(baseShippingCost, minPurchaseForFreeShipping || 0, totals.total, isConcourseUser || false);

        // Recalcular totales con el costo de envío final
        const finalTotals = calculateCartTotals(newItems, finalShippingCosts, state.hasGiftCard ? state.giftCard : null);

        set({
          items: newItems,
          countItems: finalTotals.countItems,
          iva: finalTotals.iva,
          subtotal: finalTotals.subtotal,
          total: finalTotals.total,
          lastAddedItem: payload?.titulo,
          isIncrease: true,
          shippingCosts: finalShippingCosts
        });

        return { success: true };
      },

      removeItem: (product, shippingCost, shippingCostIslands, minPurchaseForFreeShipping, isConcourseUser = false) => {
        const state = get();
        const newItems = state.items.filter((item) => !cartLinesMatch(item, product));

        if (newItems.length > 0) {
          // Calcular costo de envío base
          const baseShippingCost = calculateShippingFee('', newItems, shippingCost, shippingCostIslands);

          // Recalcular todos los totales primero para obtener el total del carrito
          const totals = calculateCartTotals(newItems, 0, state.hasGiftCard ? state.giftCard : null);

          // Aplicar envío gratuito si el total supera el mínimo o si es cliente concurso
          const finalShippingCosts = calculateShippingCosts(baseShippingCost, minPurchaseForFreeShipping || 0, totals.total, isConcourseUser);

          // Recalcular totales con el costo de envío final
          const finalTotals = calculateCartTotals(newItems, finalShippingCosts, state.hasGiftCard ? state.giftCard : null);

          set({
            items: newItems,
            countItems: finalTotals.countItems,
            iva: finalTotals.iva,
            subtotal: finalTotals.subtotal,
            total: finalTotals.total,
            lastRemovedItem: product.titulo,
            isIncrease: false,
            shippingCosts: finalShippingCosts
          });
        } else {
          // Limpiar carrito completamente pero mantener tarjeta regalo si existe
          const state = get();
          const hasGiftCard = state.hasGiftCard;
          const giftCard = state.giftCard;
          
          if (hasGiftCard && giftCard.discount_amount > 0) {
            // Si hay tarjeta regalo, recalcular totales solo con la tarjeta regalo
            const totals = calculateCartTotals([], 0, giftCard);
            
            set({
              ...initialState,
              items: initialState.items,
              countItems: totals.countItems,
              subtotal: totals.subtotal,
              iva: totals.iva,
              total: totals.total,
              shippingCosts: initialState.shippingCosts,
              discountCart: initialState.discountCart,
              shippingCartAddress: {},
              billingCartAddress: {},
              isNewShippingAddress: false,
              isStorePickup: false,
              errorAmountPrice: initialState.errorAmountPrice,
              isGiftOrder: false,
              giftData: {},
              giftCard: giftCard,
              appliedCoupon: null,
              hasGiftCard: true,
              isShippingAvailable: initialState.isShippingAvailable
            });
          } else {
            // Si no hay tarjeta regalo, limpiar completamente
            set({
              ...initialState,
              items: initialState.items,
              countItems: initialState.countItems,
              subtotal: Number(initialState.subtotal.toFixed(4)),
              iva: Number(initialState.iva.toFixed(4)),
              total: Number(initialState.total.toFixed(4)),
              shippingCosts: initialState.shippingCosts,
              discountCart: initialState.discountCart,
              shippingCartAddress: {},
              billingCartAddress: {},
              isNewShippingAddress: false,
              isStorePickup: false,
              errorAmountPrice: initialState.errorAmountPrice,
              isGiftOrder: false,
              giftData: {},
              giftCard: {},
              appliedCoupon: null,
              hasGiftCard: initialState.hasGiftCard,
              isShippingAvailable: initialState.isShippingAvailable
            });
          }
        }
      },

      increaseQuantity: (cartLine, shippingCost, shippingCostIslands, minPurchaseForFreeShipping, province, isConcourseUser = false) => {
        const state = get();
        const itemExists = state.items.find((item) => cartLinesMatch(item, cartLine));

        if (!itemExists) return { success: false, message: 'Producto no encontrado' };

        const newItems = state.items.map(item =>
          cartLinesMatch(item, cartLine)
            ? {
                ...item,
                quantity: item.quantity + 1,
                price: cartLine.unit_price ?? item.price,
                unit_price: cartLine.unit_price ?? item.unit_price,
              }
            : item
        );

        // Calcular costo de envío base
        const baseShippingCost = calculateShippingFee(province || '', newItems, shippingCost, shippingCostIslands);

        // Recalcular todos los totales primero para obtener el total del carrito
        const totals = calculateCartTotals(newItems, 0, state.hasGiftCard ? state.giftCard : null);

        // Aplicar envío gratuito si el total supera el mínimo o si es cliente concurso
        const finalShippingCosts = calculateShippingCosts(baseShippingCost, minPurchaseForFreeShipping || 0, totals.total, isConcourseUser);

        // Recalcular totales con el costo de envío final
        const finalTotals = calculateCartTotals(newItems, finalShippingCosts, state.hasGiftCard ? state.giftCard : null);

        set({
          items: newItems,
          countItems: finalTotals.countItems,
          iva: finalTotals.iva,
          subtotal: finalTotals.subtotal,
          total: finalTotals.total,
          isIncrease: true,
          lastAddedItem: itemExists.titulo,
          shippingCosts: finalShippingCosts
        });

        return { success: true };
      },

      decreaseQuantity: (cartLine, shippingCost, shippingCostIslands, minPurchaseForFreeShipping, province, isConcourseUser = false) => {
        const state = get();
        const itemExists = state.items.find((item) => cartLinesMatch(item, cartLine));

        if (!itemExists) return;

        let newItems;
        if (itemExists.quantity === 1) {
          newItems = state.items.filter((item) => !cartLinesMatch(item, cartLine));
        } else {
          newItems = state.items.map(item => 
            cartLinesMatch(item, cartLine)
              ? { ...item, quantity: item.quantity - 1 }
              : item
          );
        }

        // Calcular costo de envío base
        const baseShippingCost = calculateShippingFee(province || '', newItems, shippingCost, shippingCostIslands);

        // Recalcular todos los totales primero para obtener el total del carrito
        const totals = calculateCartTotals(newItems, 0, state.hasGiftCard ? state.giftCard : null);

        // Aplicar envío gratuito si el total supera el mínimo o si es cliente concurso
        const finalShippingCosts = calculateShippingCosts(baseShippingCost, minPurchaseForFreeShipping || 0, totals.total, isConcourseUser);

        // Recalcular totales con el costo de envío final
        const finalTotals = calculateCartTotals(newItems, finalShippingCosts, state.hasGiftCard ? state.giftCard : null);

        set({
          items: newItems,
          countItems: finalTotals.countItems,
          iva: finalTotals.iva,
          subtotal: finalTotals.subtotal,
          total: finalTotals.total,
          isIncrease: false,
          lastRemovedItem: itemExists.titulo,
          shippingCosts: finalShippingCosts
        });
      },

      clear: () => {
        set({
          ...initialState,
          items: initialState.items,
          countItems: initialState.countItems,
          subtotal: Number(initialState.subtotal.toFixed(4)),
          iva: Number(initialState.iva.toFixed(4)),
          total: Number(initialState.total.toFixed(4)),
          discountCart: initialState.discountCart,
          shippingCosts: initialState.shippingCosts,
          shippingCartAddress: {},
          billingCartAddress: {},
          isNewShippingAddress: false,
          isStorePickup: false,
          errorAmountPrice: initialState.errorAmountPrice,
          isGiftOrder: false,
          giftData: {},
          giftCard: {},
          appliedCoupon: null,
          hasGiftCard: initialState.hasGiftCard,
          isShippingAvailable: initialState.isShippingAvailable,
          contactInfo: initialState.contactInfo
        });
      },

      setTotalWithDiscount: (total) => set({ totalWithDiscount: total }),
      
      setShippingFee: (cost) => set({ shippingCosts: Number(cost) }),
      
      setShippingCartAddress: (address) => set({ shippingCartAddress: address }),
      
      setBillingCartAddress: (address) => set({ billingCartAddress: address }),
      
      setIsShippingAvailable: (availability) => set({ isShippingAvailable: availability }),
      
      setDiscountCart: (discount) => set({ discountCart: discount }),
      
      resetDiscountCart: () => set({ discountCart: initialState.discountCart }),
      
      setIsStorePickup: (isPickup) => set({ isStorePickup: isPickup }),
      
      setErrorAmountPrice: (error) => set({ errorAmountPrice: error }),
      
      setIsGiftOrder: (isGift) => set({ isGiftOrder: isGift }),
      
      setGiftData: (data) => set({ giftData: data }),
      
      setAppliedCoupon: (coupon) => set({ appliedCoupon: coupon }),
      
      setContactInfo: (info) => set({ contactInfo: info }),
      
      setGiftCard: (giftCard) => {
        const state = get();
        
        // Recalcular todos los totales usando la nueva función que maneja tarjetas regalo
        const totals = calculateCartTotals(state.items, state.shippingCosts, giftCard);
        
        set({
          giftCard,
          countItems: totals.countItems,
          subtotal: totals.subtotal,
          iva: totals.iva,
          total: totals.total,
          lastAddedItem: 'Tarjeta regalo',
          isIncrease: true,
          hasGiftCard: true
        });
      },
      
      removeGiftCard: () => {
        const state = get();
        
        // Recalcular todos los totales sin la tarjeta regalo
        const totals = calculateCartTotals(state.items, state.shippingCosts, null);
        
        set({
          subtotal: totals.subtotal,
          iva: totals.iva,
          total: totals.total,
          countItems: totals.countItems,
          hasGiftCard: false,
          giftCard: initialState.giftCard,
          lastRemovedItem: 'Tarjeta regalo',
          isIncrease: false
        });
      },

      loadCart: (savedCart) => {
        if (savedCart && typeof savedCart === 'object') {
          const newState = { ...initialState };
          
          // Cargar items del carrito
          if (savedCart.items && Array.isArray(savedCart.items)) {
            newState.items = savedCart.items;
            newState.countItems = savedCart.items.reduce((total, item) => total + (item.quantity || 1), 0);
          }
          
          // Cargar valores monetarios
          newState.subtotal = Number(savedCart.subtotal || 0);
          newState.iva = Number(savedCart.iva || 0);
          newState.total = Number(savedCart.total || 0);
          newState.shippingCosts = Number(savedCart.shippingCosts || 0);
          newState.totalWithDiscount = Number(savedCart.totalWithDiscount || 0);
          
          // Cargar configuraciones
          newState.discountCart = savedCart.discountCart || initialState.discountCart;
          newState.isStorePickup = savedCart.isStorePickup || false;
          newState.errorAmountPrice = savedCart.errorAmountPrice || false;
          newState.isGiftOrder = savedCart.isGiftOrder || false;
          newState.hasGiftCard = savedCart.hasGiftCard || false;
          
          // Cargar datos adicionales
          newState.giftData = savedCart.giftData || {};
          newState.giftCard = savedCart.giftCard || {};
          newState.appliedCoupon = savedCart.appliedCoupon || null;
          newState.isShippingAvailable = savedCart.isShippingAvailable || initialState.isShippingAvailable;
          newState.shippingCartAddress = savedCart.shippingCartAddress || {};
          newState.billingCartAddress = savedCart.billingCartAddress || {};
          newState.contactInfo = savedCart.contactInfo || initialState.contactInfo;
          
          set(newState);
        }
      }
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        items: state.items,
        countItems: state.countItems,
        subtotal: state.subtotal,
        iva: state.iva,
        total: state.total,
        shippingCosts: state.shippingCosts,
        totalWithDiscount: state.totalWithDiscount,
        discountCart: state.discountCart,
        isStorePickup: state.isStorePickup,
        errorAmountPrice: state.errorAmountPrice,
        isGiftOrder: state.isGiftOrder,
        hasGiftCard: state.hasGiftCard,
        giftData: state.giftData,
        giftCard: state.giftCard,
        appliedCoupon: state.appliedCoupon,
        isShippingAvailable: state.isShippingAvailable,
        shippingCartAddress: state.shippingCartAddress,
        billingCartAddress: state.billingCartAddress,
        contactInfo: state.contactInfo
      })
    }
  )
);
